/**
 * The advisor's agreement to read their whole contact list.
 *
 * Confirming a field map is what starts that read, so this sits above the mapping and blocks the
 * confirm button until it is given. The wording shown is sent back with the consent and stored
 * verbatim, so `bookReadConsentText` must not be reworded as copy: the per-provider sentence about
 * note visibility is part of it, because the CRMs genuinely differ. A test pins the text.
 *
 * Once given, it shrinks to a line saying so, with the way to withdraw it: an agreement that can be
 * given here can be taken back here. While it is being saved the box shows ticked, with a line
 * saying so, instead of sitting empty and greyed.
 */
import { useId } from 'react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Label, Spinner } from '@design-system';
import { BookReadConsentWithdrawalDialog } from '@client/components/BookReadConsentWithdrawalDialog';
import { useBookReadConsentWithdrawal } from '@client/hooks/useBookReadConsentWithdrawal';
import type { useCrmConsent } from '@client/hooks/useCrmConsent';
import { fieldMapCopy } from '../lib/field-map-copy';
import { FieldMapAlert } from './field-map-alert';

export function bookReadConsentText(crmName: string, noteDisclosure: string): string {
  return (
    `To find the people in your book who may need long-term care planning, Waterlily reads every ` +
    `contact in ${crmName} and keeps the fields you map below. There is no automatic deletion ` +
    `schedule, you can ask for any person's cached data to be deleted at any time, and this data is ` +
    `never used to train a model. ${noteDisclosure}`.trim()
  );
}

interface BookReadConsentProps {
  crmName: string;
  consent: ReturnType<typeof useCrmConsent>;
}

export function BookReadConsent({ crmName, consent }: BookReadConsentProps) {
  const confirmId = useId();
  const withdrawal = useBookReadConsentWithdrawal({ crmName, consent });

  if (consent.isLoading) return null;

  if (consent.hasBookReadConsent) {
    return (
      <div className="mb-6 flex flex-col gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 sm:flex-row sm:items-center sm:justify-between">
        <span>
          You agreed
          {consent.bookReadConsentedAt
            ? ` on ${new Date(consent.bookReadConsentedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}`
            : ''}{' '}
          to
          let Waterlily read every contact in {crmName}.
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={withdrawal.ask}
        >
          Withdraw consent
        </Button>
        <BookReadConsentWithdrawalDialog
          crmName={crmName}
          withdrawal={withdrawal}
        />
      </div>
    );
  }

  const text = bookReadConsentText(crmName, consent.noteDisclosure);

  return (
    <Card className="mb-6 border-amber-300 bg-amber-50">
      <CardHeader>
        <CardTitle>Before Waterlily reads your contacts</CardTitle>
        <CardDescription className="text-gray-800">{text}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3">
          <Checkbox
            id={confirmId}
            checked={consent.isSaving}
            disabled={consent.isSaving}
            onCheckedChange={checked => {
              if (checked) void consent.give('bookRead', text);
            }}
          />
          <Label
            htmlFor={confirmId}
            className="leading-normal font-normal text-gray-900"
          >
            I confirm I have the right to share my contacts&rsquo; information with Waterlily for
            this purpose.
          </Label>
        </div>
        {consent.isSaving && (
          <p
            role="status"
            className="mt-3 flex items-center gap-2 text-sm text-gray-700"
          >
            <Spinner aria-hidden="true" />
            {fieldMapCopy.consent.saving}
          </p>
        )}
        {!!consent.error && (
          <FieldMapAlert
            title={fieldMapCopy.alerts.consentTitle}
            error={consent.error}
            className="mt-3"
          />
        )}
      </CardContent>
    </Card>
  );
}
